import React from 'react';
import PropTypes from 'prop-types';
import { Col } from '@cvent/carina/components/Col';
import Button from '@cvent/carina/components/Button';
import { injectTestId } from '@cvent/nucleus-test-automation';
import FullscreenModal from '@components/layout/FullscreenModal';
import useBreakpoints from '@hooks/useBreakpoints';
import lineClamp from '@tools/lineClamp';
import { isMobileSafari } from 'react-device-detect';
import RadioGroup from '@cvent/carina/components/RadioGroup';
import { useTranslate } from 'nucleus-text';
import { MoodThemeProvider } from '@components/blocks/BlockThemeProvider';

const useStyles = () => {
  const { isMobile } = useBreakpoints();
  return {
    title: lineClamp(2),
    content: {
      wordWrap: 'break-word'
    },
    buttonRow: {
      margin: 24,
      marginTop: 16,
      marginBottom: isMobileSafari ? 72 : 24,
      display: 'flex',
      justifyContent: isMobile ? 'center' : 'flex-end'
    }
  };
};

const ExhibitorConsentModal = ({
  title,
  questionText,
  optInId,
  optInText,
  optOutId,
  optOutText,
  selectedOption,
  handleUpdate,
  onAnswer
}) => {
  const styles = useStyles();
  const { translate } = useTranslate();
  return (
    <MoodThemeProvider>
      <FullscreenModal
        testID="exhibitor-consent-modal"
        header={<div css={styles.title}>{title}</div>}
        disableFocusTrap
        content={
          <p
            css={styles.content}
            {...injectTestId('exhibitor-consent-question-body')}
          >
            {questionText}

            <br />
            <Col>
              <RadioGroup
                name={'ConsentAnswerOptions'}
                options={[
                  {
                    label: optInText,
                    value: optInId
                  },
                  {
                    label: optOutText,
                    value: optOutId
                  }
                ]}
                selected={selectedOption}
                onUpdate={handleUpdate}
                testID="exhibitor-consent-radio"
                accessibilityLabel={translate(
                  'ExhibitorConsent-AccessibilityLabel'
                )}
              />
            </Col>
          </p>
        }
        footer={
          <div css={styles.buttonRow}>
            <Col width="content">
              <Button
                testID="exhibitor-consent-continue"
                text={translate('ExhibitorConsent-ContinueButton')}
                appearance="filled"
                onClick={onAnswer}
                disabled={!selectedOption} // If the answer is not selected, keep the continue button disabled.
              />
            </Col>
          </div>
        }
      />
    </MoodThemeProvider>
  );
};

ExhibitorConsentModal.propTypes = {
  title: PropTypes.string.isRequired,
  questionText: PropTypes.string.isRequired,
  optInId: PropTypes.string.isRequired,
  optInText: PropTypes.string.isRequired,
  optOutId: PropTypes.string.isRequired,
  optOutText: PropTypes.string.isRequired,
  selectedOption: PropTypes.string,
  handleUpdate: PropTypes.func.isRequired,
  onAnswer: PropTypes.func.isRequired
};

export default ExhibitorConsentModal;
